import React, { useEffect, useMemo, useRef, useState } from 'react';
import adminActivitiesApi from './adminActivitiesApi';
import ActivityList from './ActivityList';
import ActivityEditor from './ActivityEditor';
import { STATUS_META, summaryFromDetail } from './activityAdminShared';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'live', label: STATUS_META.live.label },
  { key: 'pending', label: STATUS_META.pending.label },
  { key: 'hidden', label: STATUS_META.hidden.label },
];

function setUrlId(id) {
  const url = id ? `${window.location.pathname}?id=${id}` : window.location.pathname;
  window.history.replaceState(window.history.state, '', url);
}

export default function AdminActivities({
  activities: initialActivities,
  tags,
  gameTagId,
  gameTypes,
  basePath,
  adminDashboardUrl,
  activitiesUrl,
  initialId,
}) {
  const api = useMemo(() => adminActivitiesApi(basePath), [basePath]);
  const [activities, setActivities] = useState(initialActivities || []);
  const [filter, setFilter] = useState('all');
  const [gamesOnly, setGamesOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editorVersion, setEditorVersion] = useState(0);
  const [justSaved, setJustSaved] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const dirtyRef = useRef(false);

  const counts = useMemo(() => {
    const c = { all: activities.length, live: 0, pending: 0, hidden: 0 };
    activities.forEach((a) => { c[a.status] += 1; });
    return c;
  }, [activities]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return activities.filter((a) => {
      if (filter !== 'all' && a.status !== filter) return false;
      if (gamesOnly && !a.game_type && !a.tag_ids.includes(gameTagId)) return false;
      if (!q) return true;
      return [a.title, a.body, a.focus, a.author && a.author.name, String(a.id)]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [activities, filter, gamesOnly, query, gameTagId]);

  useEffect(() => {
    if (initialId) openActivity(initialId, { force: true });
  }, []);

  useEffect(() => {
    const warn = (e) => {
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  function confirmDiscard() {
    return !dirtyRef.current || window.confirm('You have unsaved changes. Discard them?');
  }

  async function openActivity(id, { force = false } = {}) {
    if (!force && id === selectedId) return;
    if (!force && !confirmDiscard()) return;
    dirtyRef.current = false;
    setSelectedId(id);
    setDetail(null);
    setJustSaved(false);
    setLoadError(null);
    setLoadingDetail(true);
    setUrlId(id);
    try {
      const data = await api.get(id);
      setDetail(data);
      setEditorVersion((v) => v + 1);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeEditor() {
    if (!confirmDiscard()) return;
    dirtyRef.current = false;
    setSelectedId(null);
    setDetail(null);
    setUrlId(null);
  }

  // `resetDraft` is true after a save; picture/teaser changes keep unsaved text edits.
  function handleDetailChange(next, { resetDraft = false } = {}) {
    setDetail(next);
    if (resetDraft) {
      setJustSaved(true);
      setEditorVersion((v) => v + 1);
    }
    const summary = summaryFromDetail(next);
    setActivities((list) => list.map((a) => (a.id === next.id ? summary : a)));
  }

  function handleDeleted(id) {
    dirtyRef.current = false;
    setActivities((list) => list.filter((a) => a.id !== id));
    setSelectedId(null);
    setDetail(null);
    setUrlId(null);
  }

  async function createActivity(e) {
    e.preventDefault();
    if (!newTitle.trim() || creating) return;
    if (!confirmDiscard()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await api.create({ title: newTitle.trim() });
      dirtyRef.current = false;
      setActivities((list) => [summaryFromDetail(created), ...list]);
      setFilter('all');
      setQuery('');
      setGamesOnly(false);
      setShowNew(false);
      setNewTitle('');
      setSelectedId(created.id);
      setDetail(created);
      setJustSaved(false);
      setEditorVersion((v) => v + 1);
      setUrlId(created.id);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="aaPage">
      <header className="aaHeader">
        <div>
          <a className="aaHeader__back" href={adminDashboardUrl}>← Admin dashboard</a>
          <h1 className="aaHeader__title">Activities</h1>
          <p className="aaHeader__subtitle">
            Everything on <a href={activitiesUrl}>Activities</a> and in the game picker. Only <strong>Live</strong> activities are shown to users.
          </p>
        </div>
        <button type="button" className="aaButton aaButton--primary" onClick={() => setShowNew((v) => !v)}>
          + New activity
        </button>
      </header>

      {showNew && (
        <form className="aaNew" onSubmit={createActivity}>
          <input
            className="aaInput"
            autoFocus
            placeholder="Activity title, e.g. Guess Who: Animals"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            maxLength={255}
          />
          <button type="submit" className="aaButton aaButton--primary" disabled={!newTitle.trim() || creating}>
            {creating ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="aaButton aaButton--ghost" onClick={() => setShowNew(false)}>Cancel</button>
          <p className="aaNew__hint">New activities start <strong>Hidden</strong>, so you can finish them before going live.</p>
          {createError && <p className="aaError">{createError}</p>}
        </form>
      )}

      <div className="aaToolbar">
        <div className="aaChips" role="tablist" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              className={`aaChip${filter === f.key ? ' aaChip--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="aaChip__count">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <label className="aaCheck">
          <input type="checkbox" checked={gamesOnly} onChange={(e) => setGamesOnly(e.target.checked)} />
          🎮 Games only
        </label>
        <input
          className="aaInput aaToolbar__search"
          type="search"
          placeholder="Search title, directions, author or id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className={`aaLayout${selectedId ? ' aaLayout--editing' : ''}`}>
        <ActivityList
          activities={visible}
          tags={tags}
          gameTagId={gameTagId}
          gameTypes={gameTypes}
          selectedId={selectedId}
          onSelect={(id) => openActivity(id)}
        />

        <section className="aaPanel" aria-live="polite">
          {!selectedId && (
            <div className="aaEmpty">
              <span className="aaEmpty__emoji" aria-hidden="true">🗂️</span>
              <p>Select an activity to edit it, or create a new one.</p>
            </div>
          )}
          {selectedId && loadingDetail && <div className="aaEmpty"><p>Loading…</p></div>}
          {selectedId && loadError && (
            <div className="aaEmpty">
              <p className="aaError">{loadError}</p>
              <button type="button" className="aaButton" onClick={() => openActivity(selectedId, { force: true })}>Try again</button>
            </div>
          )}
          {detail && (
            <ActivityEditor
              key={`${detail.id}-${editorVersion}`}
              api={api}
              detail={detail}
              tags={tags}
              gameTagId={gameTagId}
              gameTypes={gameTypes}
              justSaved={justSaved}
              onClose={closeEditor}
              onDetailChange={handleDetailChange}
              onDeleted={handleDeleted}
              onDirtyChange={(dirty) => { dirtyRef.current = dirty; }}
            />
          )}
        </section>
      </div>
    </div>
  );
}
